/**
 * Kanban / Board-Sicht (CompanyBrain Welle 3d, BEN-107).
 * Hooks fuer das Board (Arbeitspakete/Runs/Entscheidungen/eigene Karten), Statistik und
 * die App-eigenen Mutationen (anlegen/aendern/verschieben/loeschen). Persistenz Orchestrator
 * (Postgres, tenant+user-scoped). OpenProject-Arbeitspakete sind read-only.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenant } from "./tenant";

export type KanbanSource = "wp" | "run" | "decision" | "naechste" | "eigen";

export type Lane = { key: string; label: string; enabled?: boolean; wip_limit?: number };
export type KanbanBoardConfig = {
  user_sub: string;
  view_id: string;
  columns: Lane[];
  swimlanes_enabled: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  persistent: boolean;
};
export type KanbanLink = { label: string; href: string; kind?: string };

export type KanbanCard = {
  id: string;
  source: KanbanSource;
  kind?: string;
  ref_id?: string;
  title: string;
  body?: string;
  lane: string;
  ressort?: string;
  tp?: string;
  phase?: string;
  programm?: string;
  wp_type?: string;
  status_raw?: string;
  status_gruppe?: string;
  due_date?: string;
  priority?: string;
  category?: string;
  links?: KanbanLink[];
  readonly?: boolean;
  movable?: boolean;
  intervention?: { code: string; name: string; route: string } | null;
  sort_order?: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type KanbanColumn = {
  key: string;
  label: string;
  count: number;
  wip_limit: number;
  wip_exceeded: boolean;
  cards: KanbanCard[];
};

export type KanbanBoard = {
  tenant_id: string;
  role: "berater" | "kunde";
  generated_at: string;
  degraded: boolean;
  lanes: Lane[];
  columns: KanbanColumn[];
  config: KanbanBoardConfig;
  swimlanes: { key: string; label: string; count: number; by_lane: Record<string, number> }[];
  hidden_count: number;
  total: number;
  source_counts: Record<string, number>;
  filters: { ressort: string; programm: string; type: string; q: string };
  available_filters: { ressorts: string[]; programme: string[]; types: string[] };
  wp_available: boolean;
  persistent: boolean;
  _meta?: Record<string, unknown>;
};

export type KanbanStats = {
  own_total: number;
  by_lane: Record<string, number>;
  overdue: number;
  approvals_total: number;
  approvals_open: number;
  approvals_waiting_op: number;
};

export type WritebackApproval = {
  id: string;
  user_sub: string;
  work_package_id: string;
  title: string;
  from_lane: string;
  to_lane: string;
  from_status: string;
  to_status: string;
  reason: string;
  status: "offen" | "freigegeben_wartet_auf_op_write" | "abgelehnt" | "storniert";
  decided_by?: string;
  decision_note?: string;
  created_at: string;
  decided_at?: string | null;
  op_write_executed: false;
};

export type WritebackStats = { approvals_total: number; approvals_open: number; approvals_waiting_op: number };

export type CardInput = {
  title?: string;
  body?: string;
  lane?: string;
  ressort?: string;
  phase?: string;
  tp?: string;
  due_date?: string;
  links?: KanbanLink[];
  meta?: Record<string, unknown>;
};

export type MoveResult = {
  ok: boolean;
  gated?: boolean;
  dry_run?: boolean;
  persistent?: boolean;
  reason?: string;
  target_lane?: string;
  source?: string;
  card?: KanbanCard;
  approval?: WritebackApproval;
  op_write_executed?: false;
};

export type BoardFilters = {
  ressort?: string;
  programm?: string;
  type?: string;
  q?: string;
  include_next?: boolean;
  view?: string;
};

export const SOURCE_LABEL: Record<KanbanSource, string> = {
  wp: "Arbeitspaket",
  run: "Run",
  decision: "Entscheidung",
  naechste: "Nächster Schritt",
  eigen: "Eigene Karte",
};

function boardQuery(role: "berater" | "kunde", f: BoardFilters): string {
  const p = new URLSearchParams();
  p.set("role", role);
  if (f.ressort) p.set("ressort", f.ressort);
  if (f.programm) p.set("programm", f.programm);
  if (f.type) p.set("type", f.type);
  if (f.q) p.set("q", f.q);
  p.set("view", f.view || "default");
  p.set("include_next", f.include_next === false ? "0" : "1");
  return p.toString();
}

export function useKanbanBoard(role: "berater" | "kunde", filters: BoardFilters) {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["kanban-board", effectiveTenantId, role, filters],
    queryFn: () => api.get<KanbanBoard>(`/kanban/board?${boardQuery(role, filters)}`),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 15_000,
  });
}

export function useKanbanStats() {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["kanban-stats", effectiveTenantId],
    queryFn: () => api.get<KanbanStats & { tenant_id: string }>("/kanban/stats"),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 30_000,
  });
}

function useInvalidateBoard() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["kanban-board"] });
    qc.invalidateQueries({ queryKey: ["kanban-stats"] });
    qc.invalidateQueries({ queryKey: ["kanban-approvals"] });
    qc.invalidateQueries({ queryKey: ["next-actions"] });
  };
}

export function useCreateCard() {
  const api = useApi();
  const invalidate = useInvalidateBoard();
  return useMutation({
    mutationFn: (input: CardInput) => api.post<{ ok: boolean; card: KanbanCard }>("/kanban/cards", input),
    onSuccess: invalidate,
  });
}

export function useUpdateCard() {
  const api = useApi();
  const invalidate = useInvalidateBoard();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CardInput }) =>
      api.put<{ ok: boolean; card: KanbanCard }>(`/kanban/cards/${encodeURIComponent(id)}`, input),
    onSuccess: invalidate,
  });
}

export function useMoveCard() {
  const api = useApi();
  const invalidate = useInvalidateBoard();
  return useMutation({
    mutationFn: ({ id, lane, reason, view_id }: { id: string; lane: string; reason?: string; view_id?: string }) =>
      api.post<MoveResult>(`/kanban/cards/${encodeURIComponent(id)}/move`, { lane, reason, view_id }),
    onSuccess: invalidate,
  });
}

export function useDeleteCard() {
  const api = useApi();
  const invalidate = useInvalidateBoard();
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean; id: string }>(`/kanban/cards/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}

export function useSaveKanbanConfig() {
  const api = useApi();
  const invalidate = useInvalidateBoard();
  return useMutation({
    mutationFn: ({ view, config }: { view: string; config: Pick<KanbanBoardConfig, "columns" | "swimlanes_enabled"> }) =>
      api.put<{ ok: boolean; config: KanbanBoardConfig }>(
        `/kanban/config?view=${encodeURIComponent(view)}`, config),
    onSuccess: invalidate,
  });
}

export function useReorderCards() {
  const api = useApi();
  const invalidate = useInvalidateBoard();
  return useMutation({
    mutationFn: ({ lane, ordered_ids, view_id }: { lane: string; ordered_ids: string[]; view_id?: string }) =>
      api.post<{ ok: boolean; lane: string; ordered_ids: string[] }>("/kanban/reorder", {
        lane, ordered_ids, view_id: view_id || "default",
      }),
    onSuccess: invalidate,
  });
}

export function useWritebackApprovals(status: string = "all") {
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  return useQuery({
    queryKey: ["kanban-approvals", effectiveTenantId, status],
    queryFn: () => api.get<{
      count: number;
      items: WritebackApproval[];
      stats: WritebackStats;
      op_write_enabled: boolean;
      op_write_implemented: false;
    }>(`/kanban/writeback-approvals?status=${encodeURIComponent(status)}&limit=300`),
    enabled: Boolean(effectiveTenantId),
    retry: 1,
    staleTime: 15_000,
  });
}

export function useDecideWritebackApproval() {
  const api = useApi();
  const invalidate = useInvalidateBoard();
  return useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: "approve" | "reject"; note?: string }) =>
      api.post<{ ok: boolean; approval: WritebackApproval; op_write_executed: false }>(
        `/kanban/writeback-approvals/${encodeURIComponent(id)}/decision`, { action, note }),
    onSuccess: invalidate,
  });
}
